import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useCurrentRefinements } from 'react-instantsearch'
import { useInView } from 'react-intersection-observer'
import { isEmpty } from 'remeda'
import { useMediaQuery } from 'usehooks-ts'
import { useCategoriesGrid } from '@components/CategoriesGrid/CategoriesGridProvider'
import { useCategoryManager } from '@velasca/hooks/use-category-manager'
import { useHeader } from '@velasca/hooks/use-header'
import type { AttributeForFacetingConfig } from '@velasca/types'
import { FrontImageToggler } from '@velasca/ui/front-image-toggler'
import { SansText } from '@velasca/ui/sans-text'
import { scrollTo } from '@velasca/utils/scroll-to'
import { ActiveRefinements } from './ActiveRefinements'
import { useAlgoliaFilters } from './AlgoliaSearchProvider'
import { Facets } from './Facets'
import { FacetsDrawer } from './FacetsDrawer'
import { HiddenRefinementWidgets } from './HiddenRefinementWidgets'
import {
  Container,
  DesktopFilterButtonContainer,
  MobileControlsContainer,
  MobileFilterButton,
  MobileFilterButtonContainer,
  OpenFilterButton,
  StickyFrontImageToggler,
} from './styles'

const FILTERS_PANEL_CONTAINER_ID = 'filters-panel-container'

const categoryWrapper = document.querySelector<HTMLElement>(
  '[data-products-count]',
)

const mobileModalEl = document.querySelector<HTMLElement>('.mobile-modal')

export const attributesForFaceting: AttributeForFacetingConfig[] = [
  {
    attribute: 'colors_filter',
    locale: 'filterColor',
    handle: 'color',
  },
  {
    attribute: 'materials_filter',
    locale: 'filterMaterial',
    handle: 'material',
    limit: 100,
  },
  {
    attribute: 'available_sizes_object',
    locale: 'filterSize',
    handle: 'size',
    limit: 100,
  },
  {
    attribute: 'product_type_filter',
    locale: 'filterModel',
    handle: 'model',
    limit: 100,
  },
  {
    attribute: 'season_name',
    locale: 'filterSeason',
    handle: 'season',
    limit: 100,
  },
  {
    attribute: 'collections.handle',
    locale: 'filterCategories',
    handle: 'categories',
    limit: 100,
  },
  {
    attribute: 'soles_filter',
    locale: 'filterSole',
    handle: 'sole',
    limit: 100,
  },
]

interface AlgoliaFiltersProps {
  alwaysShowsActiveRefinements?: boolean
}

export const AlgoliaFilters = ({
  alwaysShowsActiveRefinements = false,
}: AlgoliaFiltersProps) => {
  const headerHeight = useHeader((s) => s.headerHeight)

  const { canRefine, items } = useCurrentRefinements()
  const { setIsMenuOpen, setIsSearchActive } = useAlgoliaFilters()
  const { t } = useTranslation('collection-filters')
  const isDesktopViewport = useMediaQuery('(min-width: 992px)')
  const [filtersPanelContainerRef, isFiltersPanelContainerInView] = useInView({
    initialInView: true,
  })

  const [topOfPageRef, isTopOfPageInView] = useInView({
    rootMargin: `-${headerHeight}px 0px 0px 0px`,
  })

  const headerWithoutHelpMenuHeight = isDesktopViewport
    ? headerHeight - 90
    : headerHeight + 15

  useEffect(() => {
    if (categoryWrapper === null) {
      return
    }

    if (canRefine) {
      categoryWrapper.style.minHeight = 'auto'
    } else {
      categoryWrapper.removeAttribute('style')
    }
  }, [canRefine])

  const selectedFilters = useMemo(() => {
    let count = 0

    items.forEach((item) => {
      if (item.refinements && !isEmpty(item.refinements)) {
        count = count + item.refinements.length
      }
    })

    return count
  }, [items])

  const { currentCategory } = useCategoryManager()

  const { setFrontImage, frontImage } = useCategoriesGrid()

  return (
    <>
      {/* Hidden widgets to ensure refinements work even when panels aren't rendered */}
      <HiddenRefinementWidgets />

      {!isDesktopViewport && (
        <>
          <MobileFilterButtonContainer>
            <MobileFilterButton
              onClick={() => {
                setIsMenuOpen(true)
                setIsSearchActive(true)
              }}
            >
              <SansText fontWeight="500">
                {t('filterBy')}
                {selectedFilters > 0 && ` (${selectedFilters})`}
              </SansText>
            </MobileFilterButton>

            <FrontImageToggler
              image={frontImage}
              productCategory={currentCategory.productCategory}
              onToggle={() => {
                setFrontImage(
                  frontImage === 'standard' ? 'reverse' : 'standard',
                )
              }}
            />
          </MobileFilterButtonContainer>

          {!isEmpty(items) && <ActiveRefinements isSticky={false} />}
        </>
      )}

      <Container
        topDistance={headerWithoutHelpMenuHeight}
        ref={filtersPanelContainerRef}
        id={FILTERS_PANEL_CONTAINER_ID}
        isSticky={!isTopOfPageInView}
      >
        <AnimatePresence>
          {!isDesktopViewport && !isTopOfPageInView && (
            <motion.div
              initial={{ opacity: 0, translateY: -20 }}
              animate={{ opacity: 1, y: 0, translateY: 0 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
              exit={{ opacity: 0, translateY: -20 }}
            >
              <MobileControlsContainer>
                <OpenFilterButton
                  onClick={() => {
                    setIsMenuOpen(true)
                    setIsSearchActive(true)
                  }}
                >
                  <SansText fontWeight="500">
                    {t('filterBy')}
                    {selectedFilters > 0 && ` (${selectedFilters})`}
                  </SansText>
                </OpenFilterButton>
                <StickyFrontImageToggler>
                  <FrontImageToggler
                    image={frontImage}
                    productCategory={currentCategory.productCategory}
                    onToggle={() => {
                      setFrontImage(
                        frontImage === 'standard' ? 'reverse' : 'standard',
                      )
                    }}
                  />
                </StickyFrontImageToggler>
              </MobileControlsContainer>
            </motion.div>
          )}
        </AnimatePresence>
        {isDesktopViewport && <Facets panels={attributesForFaceting} />}
        {!isDesktopViewport &&
          mobileModalEl &&
          createPortal(
            <FacetsDrawer panels={attributesForFaceting} />,
            mobileModalEl,
          )}
        {isDesktopViewport &&
          alwaysShowsActiveRefinements &&
          !isEmpty(items) && <ActiveRefinements isSticky={false} />}
      </Container>
      <span ref={topOfPageRef}></span>
      {isDesktopViewport && (
        <AnimatePresence>
          {!isFiltersPanelContainerInView && (
            <DesktopFilterButtonContainer
              topDistance={headerWithoutHelpMenuHeight}
              initial={{ opacity: 0, y: -headerWithoutHelpMenuHeight }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
              exit={{ opacity: 0, y: -headerWithoutHelpMenuHeight }}
            >
              <OpenFilterButton
                onClick={() => {
                  scrollTo(
                    `#${FILTERS_PANEL_CONTAINER_ID}`,
                    headerWithoutHelpMenuHeight,
                  )
                }}
              >
                {t('filterBy')}
              </OpenFilterButton>
              <StickyFrontImageToggler>
                <FrontImageToggler
                  image={frontImage}
                  productCategory={currentCategory.productCategory}
                  onToggle={() => {
                    setFrontImage(
                      frontImage === 'standard' ? 'reverse' : 'standard',
                    )
                  }}
                />
              </StickyFrontImageToggler>
            </DesktopFilterButtonContainer>
          )}
        </AnimatePresence>
      )}
    </>
  )
}
